import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';

export interface InvitationsListDialogData {
  boq_id: number;
  boq_code?: string;
  boq_name?: string;
  focusInviteMore?: boolean;
}

@Component({
  selector: 'app-invitations-list-dialog',
  templateUrl: './invitations-list-dialog.component.html',
  styleUrls: ['./invitations-list-dialog.component.css']
})
export class InvitationsListDialogComponent implements OnInit {
  invitations: any[] = [];
  loading = true;
  consultancyOrgs: any[] = [];
  orgsLoading = false;
  submitting = false;
  inviteForm: FormGroup;

  constructor(
    private apiService: ApiService,
    private toastr: ToastrMsgService,
    private router: Router,
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<InvitationsListDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: InvitationsListDialogData
  ) {
    this.inviteForm = this.fb.group({
      invited_org_ids: [[]],
      invited_emails: [[]]
    });
  }

  ngOnInit(): void {
    this.loadInvitations();
    this.loadConsultancyOrganizations();
  }

  get activeInvitation(): any | null {
    return this.invitations.find((inv) => this.canInviteMore(inv)) || null;
  }

  get invitedOrgIds(): number[] {
    const details = this.activeInvitation?.invited_details || [];
    return details.filter((d: any) => d.type === 'org' && d.org_id != null).map((d: any) => Number(d.org_id));
  }

  get availableOrgs(): any[] {
    const taken = new Set(this.invitedOrgIds);
    return (this.consultancyOrgs || []).filter((org) => !taken.has(Number(org.org_id)));
  }

  get inviteCount(): number {
    const orgIds = this.inviteForm.get('invited_org_ids')?.value || [];
    const emails = this.inviteForm.get('invited_emails')?.value || [];
    const validEmails = emails.filter((e: any) => typeof e === 'string' && e.includes('@'));
    return (Array.isArray(orgIds) ? orgIds.length : 0) + validEmails.length;
  }

  canInviteMore(inv: any): boolean {
    if (!inv || inv.status !== 'active') return false;
    if (inv.expires_at && new Date(inv.expires_at).getTime() < Date.now()) return false;
    return true;
  }

  loadConsultancyOrganizations(): void {
    this.orgsLoading = true;
    this.apiService.getConsultancyOrganizations().subscribe({
      next: (res: any) => {
        this.consultancyOrgs = res.data || [];
        this.orgsLoading = false;
      },
      error: () => {
        this.consultancyOrgs = [];
        this.orgsLoading = false;
      }
    });
  }

  loadInvitations(): void {
    this.loading = true;
    this.apiService.getBiddingInvitationsByBoq(this.data.boq_id).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.invitations = res.data || [];
        }
        this.loading = false;
        if (this.data.focusInviteMore) {
          setTimeout(() => {
            document.getElementById('invite-more-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 50);
        }
      },
      error: (err) => {
        this.toastr.error(err.error?.error || 'Failed to load invitations');
        this.loading = false;
      }
    });
  }

  submitMoreInvitees(): void {
    const invitation = this.activeInvitation;
    if (!invitation) {
      this.toastr.error('No open bidding invitation to add bidders to');
      return;
    }
    this.inviteForm.markAllAsTouched();
    if (this.inviteCount === 0) {
      this.toastr.error('Please select at least one organization or add at least one email');
      return;
    }

    const invitedOrgIds = this.inviteForm.get('invited_org_ids')?.value || [];
    const emailsRaw = this.inviteForm.get('invited_emails')?.value || [];
    const invitedEmails = emailsRaw
      .filter((e: any) => typeof e === 'string' && e.includes('@'))
      .map((e: string) => e.trim().toLowerCase());

    this.submitting = true;
    this.apiService.addBiddingInvitees(invitation.id, {
      invited_org_ids: invitedOrgIds,
      invited_emails: invitedEmails.length ? invitedEmails : undefined
    }).subscribe({
      next: (res: any) => {
        this.submitting = false;
        this.toastr.success(res.message || 'Additional bidders invited');
        this.inviteForm.reset({ invited_org_ids: [], invited_emails: [] });
        this.loadInvitations();
      },
      error: (err) => {
        this.submitting = false;
        this.toastr.error(err.error?.error || 'Failed to invite additional bidders');
      }
    });
  }

  viewBids(invitation: any): void {
    this.dialogRef.close({ openedBids: true });
    this.router.navigate(['/home/boq-bidding/view-bids', invitation.id]);
  }

  onClose(): void {
    this.dialogRef.close({ refreshed: true });
  }
}
